import { createReducer } from 'redux-create-reducer'
import type { Playlist } from 'types/playlist'
import {
  LEAK_TRACING_NOTIFICATION_RECEIVE,
  WATERMARKING_LEAKS_SEARCH_PARAMS_UPDATE,
  WATERMARKING_LOAD,
  WATERMARKING_LOAD_FAIL,
  WATERMARKING_LOAD_NEXT,
  WATERMARKING_LOAD_NEXT_FAIL,
  WATERMARKING_LOAD_NEXT_SUCCESS,
  WATERMARKING_LOAD_SUCCESS,
  WATERMARKING_PLAYLISTS_SEARCH_PARAMS_UPDATE,
  WATERMARKING_UPDATE_ITEMS,
} from '@disco/constants/ActionTypes'
import { PLAYLIST, TRACK } from '@disco/constants/ItemTypes'
import type { WatermarkFilters } from '@disco/constants/WatermarkStatusesTypes'
import { updateItem } from '@disco/utils/immutable'
type LeaksItem = {
  leak_id: number
  playlist_id: number | null | undefined
  track_id: number | null | undefined
  id: number | null | undefined
  shared_with:
    | {
        user_id: number | null | undefined
        contact_id: number | null | undefined
        name: string
        email: string
      }
    | null
    | undefined
  shared_by:
    | {
        user_id: number | null | undefined
        contact_id: number | null | undefined
        name: string
        email: string
      }
    | null
    | undefined
  reported_by: {
    user_id: number
    name: string
    email: string
  }
  reported: Date
  status: string
}
export type State = {
  playlists: {
    searchTerm: string
    searching: boolean
    filters: WatermarkFilters
    items: Playlist[]
    nextPage: string | null | undefined
    error: Error | null | undefined
  }
  leaks: {
    searchTerm: string
    searching: boolean
    filters: WatermarkFilters
    items: LeaksItem[]
    nextPage: string | null | undefined
    error: Error | null | undefined
  }
}
const initialState: State = {
  playlists: {
    searchTerm: '',
    searching: false,
    filters: {
      watermarking_status: 'non-empty' as const,
    },
    items: [],
    nextPage: null,
    error: null,
  },
  leaks: {
    searchTerm: '',
    searching: false,
    filters: {
      watermarking_status: 'non-empty' as const,
    },
    items: [],
    nextPage: null,
    error: null,
  },
}

const updateState = (state, update, itemType) => {
  if (itemType === PLAYLIST) {
    return {
      ...state,
      playlists: { ...state.playlists, ...update, items: update?.items || [] },
    }
  }

  if (itemType === TRACK) {
    return {
      ...state,
      leaks: { ...state.leaks, ...update, items: update?.items || [] },
    }
  }

  return state
}

export const watermarkingReducer = createReducer<State, any>(initialState, {
  [WATERMARKING_LOAD](state, { query, filters, itemType }) {
    return updateState(
      state,
      {
        searching: true,
        searchTerm: query,
        filters,
      },
      itemType,
    )
  },

  [WATERMARKING_LOAD_SUCCESS](state, { result, itemType }) {
    const { next, results } = result
    return updateState(
      state,
      {
        searching: false,
        items: results,
        nextPage: next,
      },
      itemType,
    )
  },

  [WATERMARKING_LOAD_FAIL](state, { error, itemType }) {
    return updateState(
      state,
      {
        searching: false,
        error,
      },
      itemType,
    )
  },

  [WATERMARKING_LOAD_NEXT](state, { itemType }) {
    const update: {
      searching: boolean
      items: Record<string, any>[]
    } = {
      searching: true,
      items: [],
    }

    if (itemType === PLAYLIST) {
      update.items = state.playlists.items
    }

    if (itemType === TRACK) {
      update.items = state.leaks.items
    }

    return updateState(state, update, itemType)
  },

  [WATERMARKING_LOAD_NEXT_SUCCESS](state, { result, itemType }) {
    const { next, results } = result
    const update: {
      searching: boolean
      nextPage: string | null | undefined
      items: Record<string, any>[]
    } = {
      searching: false,
      nextPage: next,
      items: [],
    }

    if (itemType === PLAYLIST) {
      update.items = [...state.playlists.items, ...results]
    }

    if (itemType === TRACK) {
      update.items = [...state.leaks.items, ...results]
    }

    return updateState(state, update, itemType)
  },

  [WATERMARKING_LOAD_NEXT_FAIL](state, { error, itemType }) {
    return updateState(
      state,
      {
        searching: false,
        error,
      },
      itemType,
    )
  },

  [WATERMARKING_UPDATE_ITEMS](state, { items }) {
    const update: {
      items: Record<string, any>[]
    } = {
      items: [],
    }
    if (items.length === 0) return state
    items.forEach(item => {
      if (item.type === PLAYLIST) {
        update.items = state.playlists.items.map(playlist => {
          return playlist.playlist_id === item.playlist_id ? item : playlist
        })
      }

      if (item.type === TRACK) {
        update.items = state.leaks.items.map(track => {
          return track.id === item.id ? item : track
        })
      }
    })
    return updateState(state, update, items[0].type)
  },

  [WATERMARKING_PLAYLISTS_SEARCH_PARAMS_UPDATE](state, { params }) {
    return { ...state, playlists: { ...state.playlists, ...params } }
  },

  [WATERMARKING_LEAKS_SEARCH_PARAMS_UPDATE](state, { params }) {
    return { ...state, leaks: { ...state.leaks, ...params } }
  },

  [LEAK_TRACING_NOTIFICATION_RECEIVE](
    state,
    {
      notification: {
        data: { id, status, detected_watermark_data },
      },
    },
  ) {
    const updatedTraceIndex = state.leaks.items.findIndex(
      item => item.id === id,
    )
    if (updatedTraceIndex === -1) return state
    const updatedTrace = {
      // updatedTraceIndex was found by findIndex, so items[updatedTraceIndex] is defined
      ...state.leaks.items[updatedTraceIndex]!,
      status,
      detected_watermark_data,
    }
    return {
      ...state,
      leaks: {
        ...state.leaks,
        items: updateItem(state.leaks.items, updatedTraceIndex, updatedTrace),
      },
    }
  },
})
